import {
  Relation,
  workspacePermissions,
  type Permission,
  type WorkspacePermission,
} from '@customerio/hydra-permissions';
import { permissionGroupPermissions } from '@customerio/hydra-permissions/permission-groups';
import { Role as StandardRole } from '@customerio/hydra-permissions/roles';
import type { UserRole } from '@/features/roles';
import { isStandardRole, stripRedactionFromRole } from '@/services/db/role/redaction';

export type ViewerBannerRoleDefinition = {
  workspacePermissions: ReadonlySet<WorkspacePermission>;
};

export type ViewerBannerRoleDefinitions = Readonly<Record<number, ViewerBannerRoleDefinition>>;

const viewTierPermissions = new Set<Permission>([
  ...workspacePermissions.filter((permission) => permission.endsWith(`/${Relation.View}`)),
  ...Object.values(permissionGroupPermissions).flatMap((permissionsByRelation) => permissionsByRelation[Relation.View]),
]);

/**
 * A standard Viewer is always view-only. A custom role is view-only when every
 * permission it grants belongs to the permission catalog's semantic View tier:
 * explicit View relations plus technical permissions grouped into View, such as
 * creating metric exports.
 */
export function isViewOnlyWorkspaceRole(
  userRoles: readonly UserRole[] | null | undefined,
  workspaceId: number | null | undefined,
  roleDefinitions: ViewerBannerRoleDefinitions | null | undefined,
): boolean {
  if (workspaceId == null) {
    return false;
  }

  const userRole = userRoles?.find((role) => role.workspace_id === workspaceId);
  if (userRole == null) {
    return false;
  }

  if (stripRedactionFromRole(userRole.role_name) === StandardRole.Viewer) {
    return true;
  }

  if (isStandardRole(userRole.role_name)) {
    return false;
  }

  const customRole = roleDefinitions?.[userRole.role_id];
  if (customRole == null || customRole.workspacePermissions.size === 0) {
    return false;
  }

  return [...customRole.workspacePermissions].every((permission) => viewTierPermissions.has(permission));
}
